import { HttpErrorResponse } from '@angular/common/http';

import {
  CorrelationId,
  createEntityCollectionStore,
  EntityState,
  getEntityCollectionActions,
  ngrxEvent,
  PickActions,
  pipeState,
} from '@recruitee/ngrx';
import { CreateViewPayload, UpdateViewPayload, View, ViewSnapshot } from '@recruitee/views-types';

export interface ViewResponse {
  view: View;
  snapshot: ViewSnapshot | null;
}

export const viewIdSelector = (state: { view: { id: number | string } }) => state.view.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<ViewResponse>()(
  'ViewsView',
  {
    idSelector: viewIdSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, viewIdSelector);
      const createOnePessimistic = (
        state: EntityState<ViewResponse>,
        payload: { dataSourceId: string; data: CreateViewPayload } & Required<CorrelationId>,
      ) =>
        adapter.markAsPending(state, {
          entities: [payload.correlationId],
          scope: null,
        });

      return {
        fetchOne: (state, payload: { dataSourceId: string; viewId: number }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.viewId),
            next => adapter.markAsPending(next, { scope: null, entities: [payload.viewId] }),
          ),
        createOnePessimistic: createOnePessimistic,

        duplicate: createOnePessimistic,
        duplicateSuccess: defaultActions.createOnePessimisticSuccess,
        duplicateFailed: defaultActions.createOnePessimisticFailed,

        removeOnePessimistic: (state, payload: { dataSourceId: string; viewId: number }) =>
          defaultActions.removeOnePessimistic(state, { data: payload.viewId }),
        updateOnePessimistic: (state, payload: { dataSourceId: string; data: UpdateViewPayload }) =>
          adapter.markAsPending(state, { entities: [payload.data.id], scope: null }),

        saveSnapshot: (
          state,
          payload: {
            dataSourceId: string;
            viewId: View['id'];
            codename: View['codename'];
            form: CreateViewPayload;
          },
        ) => state,
        saveSnapshotSuccess: ngrxEvent<{ dataSourceId: string; viewId: View['id'] }>(),
        saveSnapshotFailed: ngrxEvent<{
          dataSourceId: string;
          viewId: View['id'];
          error: HttpErrorResponse;
        }>(),

        clearSnapshot: ngrxEvent<{
          dataSourceId: string;
          viewId: View['id'];
          codename: View['codename'];
        }>(),
        clearSnapshotSuccess: (state, payload: { dataSourceId: string; viewId: View['id'] }) => {
          const data = state.entityMap[payload.viewId];
          if (!data) return state;

          return adapter.setOne(state, { ...data, snapshot: null });
        },
        clearSnapshotFailed: ngrxEvent<{
          dataSourceId: string;
          viewId: View['id'];
          error: HttpErrorResponse;
        }>(),
      };
    },
  },
);

export const ViewSelectors = {
  ...selectors,
};
export const ViewActions: PickActions<
  typeof actions,
  | 'fetchOne'
  | 'createOnePessimistic'
  | 'removeOnePessimistic'
  | 'updateOnePessimistic'
  | 'duplicate'
  | 'saveSnapshot'
  | 'clearSnapshot'
> = actions;
export { reducer as ViewReducer, namespace as ViewNamespace };
